import {
  Container,
  ContentLayout,
  Header,
} from '@cloudscape-design/components';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import Layout from '../../../components/Layout';
import Textarea from '../../../components/Textarea';
import {
  UpdateWebsiteLegalInput,
  useCreateWebsiteLegalMutation,
  useGetWebsiteLegalQuery,
  useUpdateWebsiteLegalMutation,
} from '../../../generated/graphql';
import useNotifications from '../../../hooks/useNotifications';
import { ErrorText } from '../../../lib/ErrorTexts';
import MetaTags from '../../../lib/MetaTags';

type FormValues = {
  id?: string | null;
  imprint: string;
  disclaimer: string;
};

const schema = yup
  .object()
  .shape({
    id: yup.string().notRequired(),
    imprint: yup.string().required(ErrorText.Required),
    disclaimer: yup.string().required(ErrorText.Required),
  })
  .required();

const Legal = () => {
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {},
    resolver: yupResolver(schema),
  });

  const { data: getData } = useGetWebsiteLegalQuery();
  const [updateWebsiteLegal, { data: updateData, loading: updateLoading }] =
    useUpdateWebsiteLegalMutation();
  const [createWebsiteLegal, { loading: createLoading }] =
    useCreateWebsiteLegalMutation();

  useEffect(() => {
    let data: FormValues | undefined | null = undefined;
    if (getData && getData.listWebsiteLegal && getData.listWebsiteLegal.items)
      data = {
        ...getData.listWebsiteLegal.items[0],
      } as FormValues;
    if (updateData && updateData.updateWebsiteLegal)
      data = updateData.updateWebsiteLegal as FormValues;
    if (data) {
      methods.reset(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData, updateData, methods.reset]);

  const {
    notifications,
    notifyDeletedOrUpdated,
    notifyFailed,
    notifyInProgress,
  } = useNotifications();

  const onSubmitForm = (data: FormValues) => {
    const messageId = uuidv4();
    if (data.id) {
      notifyInProgress({
        id: messageId,
        header: 'Eintrag wird gespeichert...',
      });
      //Update entry
      updateWebsiteLegal({
        variables: { input: data as UpdateWebsiteLegalInput },
      })
        .then((data) => {
          if (data.data?.updateWebsiteLegal) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              header: 'Gespeichert',
              dismissible: true,
            });
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    } else {
      notifyInProgress({
        id: messageId,
        header: 'Eintrag wird gespeichert...',
      });
      //create a new entry
      createWebsiteLegal({ variables: { input: data } })
        .then((data) => {
          if (data.data?.createWebsiteLegal) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              header: 'Gespeichert',
              dismissible: true,
            });
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  };

  const disableSubmitButton = useMemo(() => {
    if (updateLoading || createLoading) return true;
    if (isEmpty(methods.formState.dirtyFields)) return true;
    return false;
  }, [createLoading, methods.formState, updateLoading]);

  return (
    <Layout
      notifications={notifications}
      breadcrumbs={[
        { text: 'Webseite', href: '/website' },
        { text: 'Rechtliches', href: '/website/legal' },
      ]}
    >
      <MetaTags title="Rechtliches | Theo's Bistro" />
      <ContentLayout header={<Header variant="h1">Rechtliches</Header>}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmitForm)}>
            <Container>
              <SpaceBetween direction="vertical" size="l">
                <Textarea name="imprint" rows={10} label="Impressum" stretch />
                <Textarea
                  name="disclaimer"
                  rows={10}
                  label="Haftungsausschluss"
                  stretch
                />
              </SpaceBetween>
            </Container>
            <div className="flex w-full gap-4 justify-end mt-4">
              <Button formAction="none" onClick={() => methods.reset()}>
                Zurücksetzen
              </Button>
              <Button
                variant="primary"
                disabled={disableSubmitButton}
                formAction="submit"
              >
                Speichern
              </Button>
            </div>
          </form>
        </FormProvider>
      </ContentLayout>
    </Layout>
  );
};

export default Legal;
